import { observer } from "mobx-react-lite"
import useStore from "reusable/mobx-store-utils"
import CreateClientModalStore, { CreateClientModalStoreProvider } from "./CreateClientModalStore"
import { AdvisorClientDto } from '@gap/backend/app-services/advisor/client/models/AdvisorClientDto'
import { useOrgGuardStore } from "features/main/main-router/org-guard/OrgGuardStore"
import { useOrgSubscriptionTierStore } from "features/main/main-router/org-guard/main-container/org-subscription-tiers/OrgSubscriptionTierStore"
import GapDialog from "reusable/gap-dialog/GapDialog"
import ModalState from "reusable/mobx-helpers/ModalState"
import { useEffect } from "react"
import { useCacheStore } from "cache/CacheStore"
import { themeConfig } from "@gap/shared/theme"
import InputAdornment from "@mui/material/InputAdornment"
import GapDialogBigTabs from "reusable/gap-dialog/GapDialogBigTabs/GapDialogBigTabs"
import SyncOutlined from "@mui/icons-material/SyncOutlined"
import EditOutlined from "@mui/icons-material/EditOutlined"
import CreateClientModalSyncTab from "./CreateClientModalSyncTab"
import CreateClientModalAddManually from "./CreateClientModalAddManually"
import { useXpmConnectionStore } from "features/gap-components/xero-xpm-connect/XpmConnectionStore"
import { DriveFolderUploadOutlined } from "@mui/icons-material"
import CreateClientCSVImport from "./csv-import/CreateClientCSVImport"

export interface CreateClientModalParams {
   newClientName?: string
   onCreateClientCallback: (params: AdvisorClientDto.minimalWithClientOrgId) => void
   /** Called after a CSV import adds clients. */
   onClientsImported?: () => void
   modalState: ModalState
   showCreateClientTabOnOpen?: boolean
   disableBackgroundXpmSync?: boolean
}

export default observer(function CreateClientModal(props: CreateClientModalParams) {
   const orgGuardStore = useOrgGuardStore()
   const orgSubscriptionTierStore = useOrgSubscriptionTierStore()
   const cacheStore = useCacheStore()
   const xpmConnectionStore = useXpmConnectionStore()

   const createClientModalStore = useStore((sp) => new CreateClientModalStore(sp), {
      ...props,
      orgGuardStore,
      orgSubscriptionTierStore,
      cacheStore,
      xpmConnectionStore,
      clients: cacheStore.clientsCacheStore.selectableClients
   })

   useEffect(() => {
      if (props.newClientName) {
         createClientModalStore.fields.name = props.newClientName
      }
   }, [createClientModalStore, props.newClientName])

   useEffect(() => {
      if (props.modalState.visible && !props.showCreateClientTabOnOpen && !props.disableBackgroundXpmSync) {
         createClientModalStore.triggerBackgroundXpmSyncIfConnected()
      }
   }, [createClientModalStore, props.modalState.visible, props.showCreateClientTabOnOpen, props.disableBackgroundXpmSync])

   return <GapDialog
      size="medium"
      title="Add a client"
      primary={{
         name: 'Add',
         loading: createClientModalStore.loader.isLoading,
         callback: createClientModalStore.onCreateClient,
         notCloseAutomatically: true
      }}
      cancel
      modalState={props.modalState}>
      <CreateClientModalStoreProvider store={createClientModalStore}>
         <GapDialogBigTabs
            initialTab={props.showCreateClientTabOnOpen ? 'Add manually' : createClientModalStore.syncTabLabel}
            options={[
               {
                  label: createClientModalStore.syncTabLabel,
                  endAdornment: SyncOutlined,
                  component: <CreateClientModalSyncTab />,
                  buttons: { primary: { name: 'Done' } }
               },
               {
                  label: 'Import from CSV',
                  endAdornment: DriveFolderUploadOutlined,
                  component: <CreateClientCSVImport onClientsImported={props.onClientsImported} />,
               },
               {
                  label: 'Add manually',
                  endAdornment: EditOutlined,
                  component: <CreateClientModalAddManually />,
               }
            ]} />
      </CreateClientModalStoreProvider>
   </GapDialog>
})

export const TextFieldIcon = (IconComponent: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>) => {
   return <InputAdornment position="start" sx={{ color: themeConfig.light.action.active }}>
      <IconComponent fontSize="small" />
   </InputAdornment>
}
